import { apiRequest } from './client'
import type { ActorIdentity } from '@hub-wifi/shared'

/** A single "last seen" worklist row — see `GET /api/access/last-seen`
 * (apps/api/src/routes/accessActivity.ts). NAMING/SAFETY CONSTRAINT (spec
 * Decision D3): this is a last-seen timestamp, NOT presence/occupancy — the
 * device reports a single portal with no entry/exit direction, so it is
 * impossible to know who has left the building. */
export interface LastSeenEntry {
  actorUid: string
  lastSeenAt: number
  companyUid: string | null
}

/** A single "unknown badger" reconciliation row — device users with no
 * mapped Hub identity. See `GET /api/access/unknown`. */
export interface UnknownBadger {
  deviceUserId: number
  hits: number
  lastSeenAt: number
}

/** Reuses the shared, read-time-resolved identity shape already used by
 * `/api/audit/events` (see AuditLogPage) rather than redeclaring an
 * equivalent type — same `{ name, email, companyName }` contract. */
export type ActorIdentityMap = Record<string, ActorIdentity>

export interface LastSeenResponse {
  entries: LastSeenEntry[]
  identities: ActorIdentityMap
}

export interface UnknownBadgersResponse {
  entries: UnknownBadger[]
}

export function getLastSeen(token: string, windowHours: number) {
  return apiRequest<LastSeenResponse>(`/api/access/last-seen?window=${windowHours}`, { token })
}

export function getUnknownBadgers(token: string, windowHours: number) {
  return apiRequest<UnknownBadgersResponse>(`/api/access/unknown?window=${windowHours}`, {
    token,
  })
}

/** One reader the caller is allowed to actuate (Task 5, `GET
 * /api/access/readers`). Deliberately excludes the reader's `config` —
 * device credentials never leave the API. */
export interface ActuatableReader {
  id: string
  name: string
  buildingId: string
}

export interface ListActuatableReadersResponse {
  readers: ActuatableReader[]
}

/** `POST /api/access/readers/:id/open-door` response. 202 always — this is
 * a fire-and-forget request. `deviceError: true` means the device call
 * itself threw (never reached the device); it does NOT mean the door
 * failed to open in the normal case, and its ABSENCE does not mean the
 * door opened either — `status: "allowed"` is returned identically by the
 * device whether or not the relay actually fires (see
 * docs/plans/2026-08-20-remote-door-actuation.md). Real confirmation only
 * ever comes from polling `getActuation`. */
export interface OpenDoorResponse {
  actuationId: string
  deviceError?: true
}

/** `GET /api/access/actuations/:id` response — the UI's poll target for the
 * honest confirmation states. `confirmedAt` is set only once a SecBox push
 * has arrived for this actuation; it is never optimistically set on the
 * open-door request itself. */
export interface ActuationStatus {
  id: string
  requestedAt: string
  confirmedAt: string | null
  deviceAccessEventId: number | null
}

export function listActuatableReaders(token: string) {
  return apiRequest<ListActuatableReadersResponse>('/api/access/readers', { token })
}

export function openDoor(token: string, readerId: string) {
  return apiRequest<OpenDoorResponse>(`/api/access/readers/${readerId}/open-door`, {
    method: 'POST',
    token,
  })
}

export function getActuation(token: string, actuationId: string) {
  return apiRequest<ActuationStatus>(`/api/access/actuations/${actuationId}`, { token })
}
